import { useEffect, useRef, useState } from "react";
import { Wand2, Loader2, Check, X, Undo2 } from "lucide-react";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { api, formatApiError } from "@/lib/api";
import { BUSES } from "@/lib/audioEngine";

const sign = (v) => `${v > 0 ? "+" : ""}${v.toFixed(1)}`;

export function applySuggestion(mixer, r) {
  const buses = Object.fromEntries(BUSES.map(({ id }) => {
    const b = mixer.buses[id];
    return [id, { ...b, ...(r.gains[id] != null && { gain: r.gains[id] }), ...(r.eq[id] || {}), ...(r.comp[id] || {}), ...(id in r.reverb && { reverb: r.reverb[id] }) }];
  }));
  return { ...mixer, buses, duck: { ...mixer.duck, ...r.duck } };
}

const Preview = ({ r, mixer }) => (
  <div className="space-y-1.5" data-testid="automix-preview">
    {BUSES.map((b) => (
      <div key={b.id} className="grid grid-cols-[1fr_auto_auto] gap-2 items-center text-[10px] font-mono-ui" data-testid={`automix-row-${b.id}`}>
        <span className="flex items-center gap-1.5 text-slate-300 truncate"><span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: b.color }} />{b.name.split(" / ")[0]}</span>
        <span className="text-slate-500">{r.analysis[b.id].lufs == null ? "empty" : `${r.analysis[b.id].lufs} LUFS`}</span>
        <span className={r.gains[b.id] == null ? "text-slate-600" : "text-[#FFB800]"} data-testid={`automix-gain-${b.id}`}>
          {r.gains[b.id] == null ? "—" : `${sign(mixer.buses[b.id].gain)} → ${sign(r.gains[b.id])} dB`}
        </span>
      </div>
    ))}
    <p className="text-[10px] text-slate-400 pt-1" data-testid="automix-duck-summary">
      {r.duck.on ? `Ducking ${r.duck.buses.join(" + ")} by ${r.duck.depth} dB under ${r.duck.regions.length} dialogue passage(s) · dialogue ${Math.round(r.coverage * 100)}% of runtime`
        : "No dialogue detected — no ducking needed"}
    </p>
    <p className="text-[10px] text-slate-500 italic" data-testid="automix-notes">{r.notes}</p>
  </div>
);

export const AutoMixCard = ({ projectId, clips, mixer, setMixer }) => {
  const [style, setStyle] = useState("");
  const [stage, setStage] = useState("");
  const [result, setResult] = useState(null);
  const [prev, setPrev] = useState(null);
  const poll = useRef(null);
  useEffect(() => () => clearTimeout(poll.current), []);
  const audible = clips.filter((c) => ["video", "audio", "sfx", "score"].includes(c.track));

  const wait = (jid) => api.get(`/mix/auto/${jid}`).then(({ data }) => {
    if (data.status === "processing") { setStage(data.stage); poll.current = setTimeout(() => wait(jid), 1500); return; }
    setStage("");
    if (data.status === "failed") return toast.error(data.error || "Auto mix failed");
    setResult(data.result);
  }).catch((e) => { setStage(""); toast.error(formatApiError(e)); });

  const analyse = async () => {
    setResult(null); setStage("Starting…");
    try {
      const { data } = await api.post("/mix/auto", { project_id: projectId, style: style.trim() || null,
        clips: audible.map((c) => ({ asset_id: c.asset_id, track: c.track, in_point: c.in, out_point: c.out, start: c.start })) });
      wait(data.id);
    } catch (e) { setStage(""); toast.error(formatApiError(e)); }
  };
  const apply = () => { setPrev(mixer); setMixer(applySuggestion(mixer, result)); setResult(null); toast.success("Auto mix applied"); };

  return (
    <div className="w-80 shrink-0 bg-[#13151C] border border-[#0D9488]/50 rounded-md p-2.5 flex flex-col gap-2 overflow-y-auto" data-testid="automix-card">
      <div className="flex items-center justify-between">
        <span className="text-[11px] font-semibold text-[#2DD4BF] flex items-center gap-1.5"><Wand2 className="w-3.5 h-3.5" />AI AUTO MIX</span>
        <label className="flex items-center gap-1.5 text-[10px] text-slate-400" title="Lower music under dialogue">
          Ducking<Switch checked={mixer.duck.on} disabled={!mixer.duck.regions.length} onCheckedChange={(on) => setMixer({ ...mixer, duck: { ...mixer.duck, on } })} data-testid="mixer-duck-toggle" />
        </label>
      </div>
      {!result && (<>
        <input value={style} onChange={(e) => setStyle(e.target.value)} maxLength={300} placeholder='Optional style, e.g. "punchy trailer"'
          className="bg-[#0B0C10] border border-[#2A2E3D] rounded px-2 py-1.5 text-[11px] text-slate-200 placeholder:text-slate-600 focus:outline-none focus:border-[#0D9488]" data-testid="automix-style-input" />
        <button onClick={analyse} disabled={!!stage || !audible.length} data-testid="automix-analyse-button"
          className="flex items-center justify-center gap-1.5 text-[11px] font-semibold py-1.5 rounded bg-[#0D9488] text-white hover:bg-[#0f766e] disabled:opacity-40 transition-colors">
          {stage ? <><Loader2 className="w-3 h-3 animate-spin" />{stage}</> : <><Wand2 className="w-3 h-3" />Analyse & suggest mix</>}
        </button>
        <p className="text-[10px] text-slate-500">Listens to every track, balances levels and dips music & score under dialogue. Nothing changes until you press Apply.</p>
      </>)}
      {result && (<>
        <Preview r={result} mixer={mixer} />
        <div className="grid grid-cols-2 gap-1.5">
          <button onClick={apply} data-testid="automix-apply-button" className="flex items-center justify-center gap-1 text-[11px] font-semibold py-1.5 rounded bg-[#FFB800] text-[#0B0C10]"><Check className="w-3 h-3" />Apply</button>
          <button onClick={() => setResult(null)} data-testid="automix-discard-button" className="flex items-center justify-center gap-1 text-[11px] py-1.5 rounded bg-[#0B0C10] border border-[#2A2E3D] text-slate-300"><X className="w-3 h-3" />Discard</button>
        </div>
      </>)}
      {prev && !result && (
        <button onClick={() => { setMixer(prev); setPrev(null); }} data-testid="automix-undo-button" className="flex items-center justify-center gap-1 text-[10px] py-1 rounded text-slate-400 hover:text-white">
          <Undo2 className="w-3 h-3" />Revert to my previous mix
        </button>
      )}
    </div>
  );
};
